import { Box, Button, Container, Paper, Typography } from '@mui/material'
import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'

import type { ErrorBoundaryProps, ErrorBoundaryState } from './ErrorBoundary.types'

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      errorInfo,
    })

    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }

    // Here you could also send error to a logging service like DataDog
    // For example: datadogLogs.logger.error('React Error Boundary', { error, errorInfo })
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            padding: 3,
          }}
        >
          <Container maxWidth="md">
            <Paper
              elevation={3}
              sx={{
                padding: 4,
                textAlign: 'center',
                backgroundColor: '#ffffff',
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  color: '#181D29',
                  fontWeight: 600,
                  marginBottom: 2,
                }}
              >
                Etwas ist schiefgelaufen
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  marginBottom: 3,
                  fontSize: '1.1rem',
                }}
              >
                Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie, die Seite neu zu
                laden.
              </Typography>

              {import.meta.env.DEV && this.state.error && (
                <Paper
                  sx={{
                    padding: 2,
                    marginBottom: 3,
                    backgroundColor: '#f8f8f8',
                    textAlign: 'left',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      marginBottom: 1,
                      color: '#d32f2f',
                    }}
                  >
                    Fehlerdetails (nur in Entwicklungsumgebung sichtbar):
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#181D29',
                    }}
                  >
                    {this.state.error.toString()}
                    {this.state.errorInfo && (
                      <>
                        {'\n\n'}
                        {this.state.errorInfo.componentStack}
                      </>
                    )}
                  </Typography>
                </Paper>
              )}

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.handleReload}
                  sx={{
                    paddingX: 4,
                    paddingY: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                  }}
                >
                  Seite neu laden
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={this.handleReset}
                  sx={{
                    paddingX: 4,
                    paddingY: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                  }}
                >
                  Erneut versuchen
                </Button>
              </Box>
            </Paper>
          </Container>
        </Box>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
